import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ContactFormButton from '@/components/common/ContactFormButton';

const ExitIntentOverlay: React.FC = () => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Delay activation by 5 seconds after page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleMouseLeave = useCallback(
    (e: MouseEvent) => {
      if (!isReady) return;
      if (e.clientY > 5) return;
      if (sessionStorage.getItem('exitIntentShown')) return;

      sessionStorage.setItem('exitIntentShown', 'true');
      setShowOverlay(true);
    },
    [isReady]
  );

  useEffect(() => {
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave]);

  const dismiss = () => {
    setShowOverlay(false);
  };

  return (
    <AnimatePresence>
      {showOverlay && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={dismiss}
        >
          <motion.div
            className="glass-panel backdrop-blur-lg rounded-2xl p-8 md:p-12 max-w-lg mx-4 text-center border border-white/10 shadow-2xl"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, delay: 0.05 }}
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4 leading-tight">
              Wait! Don't Leave Without a Free Strategy Session
            </h2>
            <p className="text-white/70 mb-8 text-base md:text-lg">
              Our team will analyze your business and show you exactly how AI
              can boost your growth. No commitment required.
            </p>
            <div className="flex flex-col items-center gap-4">
              <ContactFormButton size="lg" onClick={dismiss}>
                Get Your Free Strategy Session
              </ContactFormButton>
              <button
                onClick={dismiss}
                className="text-white/40 hover:text-white/60 text-sm transition-colors duration-200 underline underline-offset-2"
              >
                No thanks, I'll figure it out myself
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentOverlay;
